import React, { useContext } from 'react';
import classNames from 'classnames';
import proptypes from 'prop-types';
const { arrayOf, number, shape, string } = proptypes;
import { ExperienceContext } from '@thd-nucleus/experience-context';
import {
  Button,
  Card,
  CardContent,
  Typography,
  Badge,
} from '@one-thd/sui-atomic-components';
import { triggerEditProjectAnalytics, triggerViewEstimateAnalytics } from '../../../analytics';
import {
  DESIGN_STATUS_CREATED,
  DESIGN_STATUS_PURCHASED,
  DESIGN_STATUS_SCHEDULED
} from '../../../constants';
import { navigateToTop } from '../../../../utils/navigate';

const ENTER_ROOMS_OPTION_ID = 8208;
const COLOR_FINISH_OPTION_ID = 11337;

export const FlooringProjectCard = ({ items, designId, designIndex, status }) => {
  const { itemId, itemProduct, subtotal, retail } = items[0];
  const { choices, mainImageUrl } = itemProduct;
  const designJson = choices.find(({ optionId }) => optionId === ENTER_ROOMS_OPTION_ID)?.choiceValue || {};
  const color = choices.find(({ optionId }) => optionId === COLOR_FINISH_OPTION_ID)?.choiceValue;
  const { collection, totalArea } = JSON.parse(designJson);

  const {
    hosts: { customizableProductsBaseUrl = '' },
  } = useContext(ExperienceContext);

  const getStatusLabel = () => {
    let label = status;

    if (status.toLowerCase().includes(DESIGN_STATUS_PURCHASED)) {
      label = 'Project Purchased';
    } else if (status.toLowerCase().includes(DESIGN_STATUS_SCHEDULED)) {
      label = 'Scheduled Measure';
    }

    return label;
  };

  const badgeColor = classNames(
    {
      success: status.toLowerCase().includes(DESIGN_STATUS_PURCHASED),
      brand: status.toLowerCase().includes(DESIGN_STATUS_SCHEDULED)
    }
  );

  return (
    <div key={designIndex}>
      <Card disableGutters disablePadding grow>
        <CardContent>
          <div>
            <Typography align="left" variant="h4">Your Project</Typography>
            <div className="sui-h-8 sui-flex sui-items-center">
              {!status.toLowerCase().includes(DESIGN_STATUS_CREATED)
                && <Badge variant="regular" color={badgeColor}>{getStatusLabel(status)}</Badge>}
            </div>
          </div>
          <div className="sui-flex sui-flex-row sui-mb-4">
            <img
              src={mainImageUrl}
              height="100"
              width="100"
              className="sui-w-auto sui-aspect-square"
              alt="carpet product"
            />
            <div
              className="sui-flex sui-flex-col sui-w-2/3 sui-justify-between sui-ml-2 sui-text-base"
            >
              <span className="sui-inline">
                <div className="sui-inline sui-font-bold sui-mr-1">
                  Carpet:
                </div>
                <p className="sui-inline">
                  {collection}
                </p>
              </span>
              <span className="sui-inline">
                <div className="sui-inline sui-font-bold sui-mr-1">
                  Color:
                </div>
                <p className="sui-inline">
                  {color}
                </p>
              </span>
              <span className="sui-inline">
                <div className="sui-inline sui-font-bold sui-mr-1">
                  Total Area:
                </div>
                <p className="sui-inline">
                  {totalArea} sq. ft.
                </p>
              </span>
            </div>
          </div>
          <div className="sui-text-base sui-flex sui-flex-col sui-gap-2">
            <div className="sui-flex sui-flex-row sui-justify-between">
              <Typography variant="body-xs">Subtotal</Typography>
              <Typography variant="body-xs">${subtotal}</Typography>
            </div>
            <div className="sui-flex sui-flex-col">
              <div className="sui-flex sui-flex-row sui-justify-between">
                <Typography>Delivery</Typography>
                <Typography>---</Typography>
              </div>
              <Typography variant="body-xs">(Determined in later step)</Typography>
            </div>
            <div className="sui-flex sui-flex-col">
              <div className="sui-flex sui-flex-row sui-justify-between">
                <Typography>Basic Installations</Typography>
                <Typography>---</Typography>
              </div>
              <Typography variant="body-xs">(Determined in later step)</Typography>
            </div>
            <div className="sui-flex sui-flex-col">
              <div className="sui-flex sui-flex-row sui-justify-between">
                <Typography>Sales Tax</Typography>
                <Typography>---</Typography>
              </div>
              <Typography variant="body-xs">Sales Tax determined in later step</Typography>
            </div>
          </div>
          <hr />
          <div className="sui-flex sui-flex-row sui-justify-end sui-text-lg sui-font-bold">
            <span className="sui-mr-4">
              Total
            </span>
            <span>
              ${retail}
            </span>
          </div>
          <hr />
          <Button
            fullWidth
            onClick={() => {
              triggerViewEstimateAnalytics();
              navigateToTop(`${customizableProductsBaseUrl}/flooring/estimate/${designId}`);
            }}
          >
            View Estimate
          </Button>
          <Button
            variant="secondary"
            fullWidth
            onClick={() => {
              triggerEditProjectAnalytics();
              navigateToTop(`/custom-flooring/?item=${itemId}&isEdit`);
            }}
          >
            Edit Project
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

FlooringProjectCard.displayName = 'FlooringProjectCard';

FlooringProjectCard.propTypes = {
  items: arrayOf(shape({
    itemProduct: shape({
      choices: arrayOf(shape({
        choiceValue: string,
        optionId: number
      })),
      mainImageUrl: string,
      retail: number,
      subtotal: number
    }),
    cost: number,
    retail: number,
    subtotal: number
  })).isRequired,
  designId: string.isRequired,
  designIndex: number.isRequired,
  status: string.isRequired
};